import { Fragment, useMemo, useState, useRef, useEffect } from 'react'
import type { TimeEntry } from '@/services/timeTracking'
import type { Project } from '../../../shared/types'

interface ReportingDistributionChartProps {
  /** All time entries (unfiltered by project/location) — used to colour every cell */
  allEntries: TimeEntry[]
  allProjects: Project[]
  /** The currently selected date range — determines which cells are bright vs dimmed */
  dateRange: { startDate: string; endDate: string }
}

const CELL = 10
const GAP = 2
const STEP = CELL + GAP
const DAY_LABEL_WIDTH = 28
// Extra spacing inserted between month groups (beyond the normal GAP)
const MONTH_GAP = 4

// Selected-range empty: medium-light gray
const COLOR_EMPTY_IN_RANGE = '#E9ECEF'
// Context (outside selection): subtly dimmer
const COLOR_EMPTY_CONTEXT = '#F0F0F0'

const DAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', '']

const pad = (n: number) => String(n).padStart(2, '0')

const localDate = (s: string) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const dateToStr = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const getISODayOfWeek = (d: Date) => (d.getDay() + 6) % 7

const getTodayYearMonth = () => {
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`
}

/** Mix `hex` colour with white by `amount` (0 = original, 1 = white). */
const lightenColor = (hex: string, amount: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lr = Math.round(r + (255 - r) * amount)
  const lg = Math.round(g + (255 - g) * amount)
  const lb = Math.round(b + (255 - b) * amount)
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`
}

/**
 * Returns the CSS `background` value for a cell.
 * Single-project: solid colour (or lightened for context cells).
 * Multi-project: vertical linear-gradient with each project getting a
 * proportional slice based on hours worked.
 */
const getCellBackground = (
  breakdown: DayCell['projectBreakdown'],
  totalHours: number,
  inRange: boolean
): string => {
  if (breakdown.length === 0) {
    return inRange ? COLOR_EMPTY_IN_RANGE : COLOR_EMPTY_CONTEXT
  }
  const colorOf = (hex: string) => inRange ? hex : lightenColor(hex, 0.6)
  if (breakdown.length === 1) {
    return colorOf(breakdown[0].color)
  }
  // Smooth gradient: each project is anchored at the midpoint of its proportional
  // range so colours blend naturally into each other.
  let acc = 0
  const stops: string[] = []
  for (const pb of breakdown) {
    const start = acc / totalHours
    acc += pb.hours
    const end = acc / totalHours
    const mid = ((start + end) / 2 * 100).toFixed(1)
    stops.push(`${colorOf(pb.color)} ${mid}%`)
  }
  return `linear-gradient(to bottom, ${stops.join(', ')})`
}

interface DayCell {
  date: string
  inRange: boolean   // within selected dateRange — full-opacity rendering
  inWindow: boolean  // within visible window — renders as a cell (vs transparent padding)
  color: string | null
  totalHours: number
  projectBreakdown: Array<{ name: string; hours: number; color: string }>
}

interface TooltipState {
  cell: DayCell
  x: number
  y: number
}

const getProjectInfo = (
  projectId: string,
  allProjects: Project[]
): { name: string; color: string } => {
  if (projectId === '__none__') return { name: 'No Project', color: '#9CA3AF' }
  const p = allProjects.find(p => p.id === projectId)
  return { name: p?.name ?? 'Unknown', color: p?.color ?? '#6B7280' }
}

const formatDateLabel = (dateStr: string) => {
  const d = localDate(dateStr)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export default function ReportingDistributionChart({
  allEntries,
  allProjects,
  dateRange
}: ReportingDistributionChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const todayYearMonth = getTodayYearMonth()

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const { weeks, monthLabels, monthStartWeekIndices, projectLegend } = useMemo(() => {
    const rangeStart = localDate(dateRange.startDate)
    const rangeEnd   = localDate(dateRange.endDate)

    let gridStart: Date
    let gridEnd: Date
    let visibleStart: Date
    let visibleEnd: Date

    if (containerWidth > 0) {
      // ── Centred on range midpoint, fills container exactly ──────────────────
      // Iteratively refine totalWeeks to account for month-gap spacers.
      // ~4.33 weeks/month on average.
      let totalWeeks = Math.floor((containerWidth - DAY_LABEL_WIDTH + GAP) / STEP)
      for (let _i = 0; _i < 3; _i++) {
        const estMonths = Math.max(1, Math.ceil(totalWeeks / 4.33))
        const gapPx     = Math.max(0, estMonths - 1) * MONTH_GAP
        totalWeeks = Math.max(4, Math.floor((containerWidth - DAY_LABEL_WIDTH - gapPx + GAP) / STEP))
      }

      // Monday of the week that contains the range midpoint
      const mid = new Date((rangeStart.getTime() + rangeEnd.getTime()) / 2)
      const midMonday = new Date(mid)
      midMonday.setDate(midMonday.getDate() - getISODayOfWeek(midMonday))

      gridStart = new Date(midMonday)
      gridStart.setDate(gridStart.getDate() - Math.floor(totalWeeks / 2) * 7)
      gridEnd = new Date(gridStart)
      gridEnd.setDate(gridEnd.getDate() + totalWeeks * 7 - 1)

      // In the centred branch the full grid is the visible window (no ISO padding)
      visibleStart = gridStart
      visibleEnd   = gridEnd
    } else {
      // ── Fallback: 12-month window centred on range midpoint (used in tests) ──
      const halfWindowMs = 182.5 * 24 * 3600 * 1000
      const centerMs = (rangeStart.getTime() + rangeEnd.getTime()) / 2
      const wsRaw = new Date(centerMs - halfWindowMs)
      const weRaw = new Date(centerMs + halfWindowMs)
      const wsSnapped = new Date(wsRaw.getFullYear(), wsRaw.getMonth(), 1)
      const weSnapped = new Date(weRaw.getFullYear(), weRaw.getMonth() + 1, 0)
      const rsMonthStart = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1)
      const reMonthEnd   = new Date(rangeEnd.getFullYear(),   rangeEnd.getMonth() + 1, 0)
      visibleStart = wsSnapped < rsMonthStart ? wsSnapped : rsMonthStart
      visibleEnd   = weSnapped > reMonthEnd   ? weSnapped : reMonthEnd

      gridStart = new Date(visibleStart)
      gridStart.setDate(gridStart.getDate() - getISODayOfWeek(gridStart))
      const naturalGridEnd = new Date(visibleEnd)
      const endDow = getISODayOfWeek(naturalGridEnd)
      if (endDow < 6) naturalGridEnd.setDate(naturalGridEnd.getDate() + (6 - endDow))
      gridEnd = naturalGridEnd
    }

    const visibleStartStr = dateToStr(visibleStart)
    const gridEndStr      = dateToStr(gridEnd)

    // Build dayMap from allEntries within the visible window
    const dayMap = new Map<string, Map<string, number>>()
    for (const entry of allEntries) {
      if (!entry.endTime) continue
      const dt = entry.startTime.substring(0, 10)
      if (dt < visibleStartStr || dt > gridEndStr) continue
      if (!dayMap.has(dt)) dayMap.set(dt, new Map())
      const pid = entry.projectId ?? '__none__'
      const hours = (entry.duration ?? 0) / 3600
      const m = dayMap.get(dt)!
      m.set(pid, (m.get(pid) ?? 0) + hours)
    }

    const legendMap = new Map<string, { name: string; color: string }>()
    const weeks: DayCell[][] = []
    let currentWeek: DayCell[] = []
    const cursor = new Date(gridStart)
    const seenMonths = new Set<string>()
    const monthLabels: Array<{ label: string; weekIdx: number; yearMonth: string; isPartial: boolean }> = []

    while (cursor <= gridEnd) {
      const dateStr  = dateToStr(cursor)
      const inWindow = cursor >= visibleStart && cursor <= visibleEnd
      const inRange  = cursor >= rangeStart && cursor <= rangeEnd

      let cell: DayCell

      if (inWindow) {
        const yearMonth = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`
        const weekIdx   = weeks.length

        if (!seenMonths.has(yearMonth)) {
          seenMonths.add(yearMonth)
          // In the fallback branch, don't label partial weeks beyond visibleEnd
          if (cursor <= visibleEnd) {
            const [mlYear, mlMon] = yearMonth.split('-').map(Number)
            const firstOfMonth = new Date(mlYear, mlMon - 1, 1)
            const lastOfMonth  = new Date(mlYear, mlMon, 0)
            const isPartial =
              firstOfMonth.getTime() < gridStart.getTime() ||
              lastOfMonth.getTime()  > gridEnd.getTime()
            const label = cursor.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
            monthLabels.push({ label, weekIdx, yearMonth, isPartial })
          }
        }

        const pidMap = dayMap.get(dateStr)
        if (pidMap && pidMap.size > 0) {
          let dominantPid   = ''
          let dominantHours = 0
          let total         = 0
          for (const [pid, h] of pidMap) {
            total += h
            if (h > dominantHours) { dominantHours = h; dominantPid = pid }
          }

          const info = getProjectInfo(dominantPid, allProjects)
          if (!legendMap.has(dominantPid)) legendMap.set(dominantPid, info)

          const breakdown = Array.from(pidMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([pid, h]) => {
              const pInfo = getProjectInfo(pid, allProjects)
              if (!legendMap.has(pid)) legendMap.set(pid, pInfo)
              return { name: pInfo.name, hours: Math.round(h * 10) / 10, color: pInfo.color }
            })

          cell = {
            date: dateStr, inRange, inWindow: true,
            color: info.color,
            totalHours: Math.round(total * 10) / 10, projectBreakdown: breakdown
          }
        } else {
          cell = { date: dateStr, inRange, inWindow: true, color: null, totalHours: 0, projectBreakdown: [] }
        }
      } else {
        // Padding cell (before visibleStart due to ISO week alignment) — transparent
        cell = { date: dateStr, inRange: false, inWindow: false, color: null, totalHours: 0, projectBreakdown: [] }
      }

      currentWeek.push(cell)
      if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = [] }
      cursor.setDate(cursor.getDate() + 1)
    }

    if (currentWeek.length > 0) weeks.push(currentWeek)

    const monthStartWeekIndices = new Set(monthLabels.slice(1).map(ml => ml.weekIdx))
    const projectLegend = Array.from(legendMap.entries()).map(([id, info]) => ({ id, ...info }))
    return { weeks, monthLabels, monthStartWeekIndices, projectLegend }
  }, [allEntries, allProjects, dateRange, containerWidth])

  const numWeeks = weeks.length

  const handleMouseEnter = (cell: DayCell, e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || cell.projectBreakdown.length === 0) return
    const containerRect = containerRef.current.getBoundingClientRect()
    const cellRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
    setTooltip({
      cell,
      x: cellRect.left - containerRect.left + cellRect.width / 2,
      y: cellRect.top - containerRect.top
    })
  }

  const handleMouseLeave = () => setTooltip(null)

  return (
    // The outer div is the tooltip anchor (relative) and the ResizeObserver target.
    // overflow:hidden lives on the inner grid div only — keeping it off the outer div
    // prevents the tooltip from being clipped when it extends above the grid rows.
    <div
      data-testid="reporting-distribution-chart"
      className="relative"
      ref={containerRef}
      onMouseLeave={handleMouseLeave}
    >
      <div className="overflow-hidden">
        {/* Month labels — one div per month, with (MONTH_GAP+GAP) spacers between to
            match the marginLeft applied to the first week column of each new month. */}
        <div style={{ display: 'flex', paddingLeft: DAY_LABEL_WIDTH, marginBottom: 2, height: 12 }}>
          {monthLabels[0]?.weekIdx > 0 && (
            <div style={{ width: monthLabels[0].weekIdx * STEP, flexShrink: 0 }} />
          )}
          {monthLabels.map((ml, i) => {
            const nextWeekIdx = i + 1 < monthLabels.length ? monthLabels[i + 1].weekIdx : numWeeks
            const width = (nextWeekIdx - ml.weekIdx) * STEP - GAP
            const isCurrent = ml.yearMonth === todayYearMonth
            return (
              <Fragment key={ml.yearMonth}>
                {i > 0 && <div style={{ width: MONTH_GAP + GAP, flexShrink: 0 }} />}
                <div
                  data-testid="month-label"
                  data-year-month={ml.yearMonth}
                  style={{
                    width,
                    flexShrink: 0,
                    fontSize: 9,
                    lineHeight: '12px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    color: isCurrent ? '#374151' : '#9CA3AF',
                    fontWeight: isCurrent ? 600 : 400
                  }}
                >
                  {ml.isPartial ? null : ml.label}
                </div>
              </Fragment>
            )
          })}
        </div>

        {/* Grid */}
        <div style={{ display: 'flex' }}>
          {/* Day-of-week labels */}
          <div style={{ width: DAY_LABEL_WIDTH, display: 'flex', flexDirection: 'column', gap: GAP }}>
            {DAY_LABELS.map((label, i) => (
              <div
                key={i}
                style={{ height: CELL, fontSize: 9, lineHeight: `${CELL}px` }}
                className="text-gray-400"
              >
                {label}
              </div>
            ))}
          </div>

          {/* Week columns — extra marginLeft on first week of each month creates month division */}
          <div style={{ display: 'flex', gap: GAP }}>
            {weeks.map((week, wi) => (
              <div
                key={wi}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: GAP,
                  marginLeft: monthStartWeekIndices.has(wi) ? MONTH_GAP : 0
                }}
              >
                {week.map((cell, di) => {
                  if (!cell.inWindow) {
                    return <div key={di} style={{ width: CELL, height: CELL }} />
                  }
                  const bg = getCellBackground(cell.projectBreakdown, cell.totalHours, cell.inRange)
                  return (
                    <div
                      key={di}
                      data-testid="activity-cell"
                      data-date={cell.date}
                      data-in-range={cell.inRange ? 'true' : 'false'}
                      {...(cell.color ? { 'data-color': cell.color } : {})}
                      style={{
                        width: CELL,
                        height: CELL,
                        background: bg,
                        borderRadius: 2,
                        cursor: cell.projectBreakdown.length > 0 ? 'pointer' : 'default'
                      }}
                      onMouseEnter={e => handleMouseEnter(cell, e)}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend + no-entries notice */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 items-center justify-center">
        {projectLegend.map(p => (
          <div key={p.id} className="flex items-center gap-1">
            <div style={{ width: 8, height: 8, backgroundColor: p.color, borderRadius: 1, flexShrink: 0 }} />
            <span style={{ fontSize: 11 }} className="text-gray-500">{p.name}</span>
          </div>
        ))}
        {projectLegend.length === 0 && (
          <p className="text-xs text-gray-400">No entries in selected period</p>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute z-50 bg-white border border-gray-200 rounded-md shadow-sm p-2 text-xs pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, calc(-100% - 6px))',
            minWidth: 120
          }}
        >
          <p className="font-medium text-gray-700 mb-1">{formatDateLabel(tooltip.cell.date)}</p>
          {tooltip.cell.projectBreakdown.map(pb => (
            <div key={pb.name} className="flex items-center gap-1.5 text-gray-600">
              <span
                className="inline-block flex-shrink-0"
                style={{ width: 8, height: 8, backgroundColor: pb.color, borderRadius: 1 }}
              />
              <span>{pb.name}:</span>
              <span className="font-medium">{pb.hours}h</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
